import * as Sentry from '@sentry/react'
import type { ReactNode } from 'react'
import { createContext, useContext, useState, useEffect } from 'react'

import JwtService from '../services/common/jwtService'
import getPlatform from '../utils/getPlatform'
import getUser from '../utils/getUser'
import { getToken, setToken, stripTokenFromUrl } from '../utils/tokenManager'
import { checkAtob, isEmpty } from '../utils/utils'

declare global {
    interface Window {
        appexJwt?: string | null
    }
}

export type AuthContextType = {
    user: any
    setUser: (user: any) => void
    platform: any
    isPlatformAuthed: any
    setIsPlatformAuthed: (isAuthed: boolean) => void
    setPlatform: any
    isAdmin: boolean
    appexJwt?: string | null
    setAppexJwt: (token: string) => void
    jwtUser: any
    jwtPlatform: any
}

export const AuthContext = createContext<AuthContextType>({
    user: undefined,
    setUser: (): void => {},
    platform: undefined,
    isPlatformAuthed: false,
    isAdmin: false,
    setIsPlatformAuthed: (): void => {},
    setPlatform: (): void => {},
    appexJwt: undefined,
    setAppexJwt: (): void => {},
    jwtUser: undefined,
    jwtPlatform: undefined,
})

export function useAuth(): AuthContextType {
    return useContext(AuthContext)
}

type Props = {
    children: ReactNode
}

type Platform = {
    id: string
    platform_id: string
    integration_id: string
    integrations: Record<string, unknown>
}

export function AuthProvider({ children }: Props): JSX.Element {
    const [isLoading, setIsLoading] = useState(true)
    const [isAdmin, setIsAdmin] = useState(false)
    const [user, setUser] = useState(undefined)
    const [platform, setPlatform] = useState<Platform | null>()
    const [isPlatformAuthed, setIsPlatformAuthed] = useState(false)
    const [appexJwt, setAppexJwt] = useState<string | null>()
    const [jwtPlatform, setJwtPlatform] = useState()
    const [jwtUser, setJwtUser] = useState()

    /**
     * Recovers a JWT from the session cookie.
     *
     * The tokenManager holds the JWT in memory only, so a page reload on a URL
     * without `?token=` leaves us without one. /api/v1/token is the single route
     * that still accepts the session cookie alone, and exchanges it for a JWT
     * that the rest of /api/v1 requires.
     */
    async function bootstrapToken(): Promise<void> {
        try {
            const { token } = await JwtService.refreshToken()
            if (!isEmpty(token)) {
                setToken(token)
                setAppexJwt(token)
            }
        } catch (error) {
            // Session absent or expired. Downstream calls 401 and the user is
            // sent back through the platform entry point.
            Sentry.captureException(error)
        }
    }

    async function fetchData(): Promise<any> {
        const urlParams = new URLSearchParams(window.location.search)
        const token = urlParams.get('token') as string
        if (isEmpty(getToken()) && !isEmpty(token)) {
            setToken(token)
            setAppexJwt(token)
        }
        stripTokenFromUrl()

        if (isEmpty(getToken())) {
            await bootstrapToken()
        }

        setUser(await getUser())
        setPlatform(await getPlatform())
    }

    useEffect(() => {
        if (!user) {
            return
        }
        // @ts-expect-error
        setIsAdmin(user?.email?.endsWith('@algolia.com'))
    }, [user])

    useEffect(() => {
        if (!isEmpty(appexJwt) && appexJwt) {
            const jwtParts = appexJwt.split('.')
            if (jwtParts.length > 1 && checkAtob(jwtParts[1])) {
                const decodedToken = JSON.parse(atob(jwtParts[1]))
                if (!isEmpty(decodedToken.currentPlatform)) {
                    setJwtPlatform(decodedToken.currentPlatform)
                }

                if (!isEmpty(decodedToken.currentUser)) {
                    setJwtUser(decodedToken.currentUser)
                }
            }
        }
    }, [appexJwt])

    useEffect(() => {
        setIsLoading(true)
        fetchData().finally(() => {
            setIsLoading(false)
        })
    }, [])

    return (
        <AuthContext.Provider
            value={{
                user,
                setUser,
                platform,
                setPlatform,
                isPlatformAuthed,
                setIsPlatformAuthed,
                isAdmin,
                appexJwt,
                setAppexJwt,
                jwtUser,
                jwtPlatform,
            }}
        >
            {!isLoading && children}
        </AuthContext.Provider>
    )
}
